import { supabase } from '../lib/supabase';

export const bulkOperationsService = {
  // Create bulk operation
  async createBulkOperation(operationData) {
    try {
      const { data, error } = await supabase?.from('bulk_operations')?.insert([operationData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create bulk operation error:', error);
      return { data: null, error: error?.message || 'Failed to create bulk operation' };
    }
  },

  // Get bulk operations for organization
  async getBulkOperations(organizationId, options = {}) {
    try {
      const { page = 1, limit = 20, status, operationType, dateFrom, dateTo } = options;
      const offset = (page - 1) * limit;

      let query = supabase?.from('bulk_operations')?.select(`
          *,
          created_by:created_by(full_name, email)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (status) query = query?.eq('status', status);
      if (operationType) query = query?.eq('operation_type', operationType);
      if (dateFrom) query = query?.gte('created_at', dateFrom);
      if (dateTo) query = query?.lte('created_at', dateTo);

      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get bulk operations error:', error);
      return { data: [], error: error?.message || 'Failed to fetch bulk operations' };
    }
  },

  // Update bulk operation status
  async updateBulkOperationStatus(operationId, status, results = {}) {
    try {
      const updateData = {
        status,
        ...results
      };

      if (status === 'processing') {
        updateData.started_at = new Date()?.toISOString();
      } else if (status === 'completed' || status === 'failed') {
        updateData.completed_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('bulk_operations')?.update(updateData)?.eq('id', operationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update bulk operation error:', error);
      return { data: null, error: error?.message || 'Failed to update bulk operation' };
    }
  },

  // Execute bulk account updates
  async executeBulkAccountUpdate(operationId, organizationId, filterCriteria, updateValues) {
    try {
      await this.updateBulkOperationStatus(operationId, 'processing');

      // Build the filter query for chart_of_accounts
      let query = supabase?.from('chart_of_accounts')?.select('id')?.eq('organization_id', organizationId);

      // Apply filters
      if (filterCriteria?.account_type) {
        query = query?.eq('account_type', filterCriteria?.account_type);
      }
      if (filterCriteria?.account_subtype) {
        query = query?.eq('account_subtype', filterCriteria?.account_subtype);
      }
      if (filterCriteria?.status) {
        query = query?.eq('status', filterCriteria?.status);
      }
      if (filterCriteria?.account_codes) {
        query = query?.in('account_code', filterCriteria?.account_codes);
      }

      const { data: accountsToUpdate, error: selectError } = await query;
      if (selectError) throw selectError;

      const recordsToProcess = accountsToUpdate?.length || 0;
      let recordsSucceeded = 0;
      let recordsFailed = 0;
      const errorDetails = [];

      // Update progress
      await this.updateBulkOperationStatus(operationId, 'processing', {
        records_to_process: recordsToProcess,
        records_processed: 0
      });

      // Process updates in batches
      const batchSize = 10;
      for (let i = 0; i < accountsToUpdate?.length; i += batchSize) {
        const batch = accountsToUpdate?.slice(i, i + batchSize);
        
        try {
          const { error: updateError } = await supabase?.from('chart_of_accounts')?.update({
              ...updateValues,
              updated_at: new Date()?.toISOString()
            })?.in('id', batch?.map(account => account?.id));

          if (updateError) {
            recordsFailed += batch?.length;
            errorDetails?.push({
              batch_start: i,
              batch_size: batch?.length,
              error: updateError?.message
            });
          } else {
            recordsSucceeded += batch?.length;
          }
        } catch (batchError) {
          recordsFailed += batch?.length;
          errorDetails?.push({
            batch_start: i,
            batch_size: batch?.length,
            error: batchError?.message
          });
        }

        // Update progress
        await this.updateBulkOperationStatus(operationId, 'processing', {
          records_processed: i + batch?.length,
          records_succeeded: recordsSucceeded,
          records_failed: recordsFailed
        });
      }

      // Mark as completed
      const finalStatus = recordsFailed > 0 ? 'completed' : 'completed';
      await this.updateBulkOperationStatus(operationId, finalStatus, {
        records_processed: recordsToProcess,
        records_succeeded: recordsSucceeded,
        records_failed: recordsFailed,
        error_details: errorDetails
      });

      return { 
        success: true, 
        recordsProcessed: recordsToProcess,
        recordsSucceeded,
        recordsFailed,
        errorDetails,
        error: null 
      };
    } catch (error) {
      await this.updateBulkOperationStatus(operationId, 'failed', {
        error_details: [{ error: error?.message }]
      });
      console.error('Execute bulk account update error:', error);
      return { success: false, error: error?.message || 'Bulk update failed' };
    }
  },

  // Preview bulk operation results
  async previewBulkOperation(organizationId, targetTable, filterCriteria) {
    try {
      let query = supabase?.from(targetTable)?.select('id, account_code, account_name, account_type, account_subtype, status')?.eq('organization_id', organizationId);

      // Apply same filters as actual operation
      if (filterCriteria?.account_type) {
        query = query?.eq('account_type', filterCriteria?.account_type);
      }
      if (filterCriteria?.account_subtype) {
        query = query?.eq('account_subtype', filterCriteria?.account_subtype);
      }
      if (filterCriteria?.status) {
        query = query?.eq('status', filterCriteria?.status);
      }
      if (filterCriteria?.account_codes) {
        query = query?.in('account_code', filterCriteria?.account_codes);
      }

      query = query?.limit(100); // Limit preview results

      const { data, error } = await query;
      if (error) throw error;

      return { 
        data: data || [], 
        totalCount: data?.length || 0,
        error: null 
      };
    } catch (error) {
      console.error('Preview bulk operation error:', error);
      return { data: [], totalCount: 0, error: error?.message || 'Failed to preview operation' };
    }
  },

  // Cancel bulk operation
  async cancelBulkOperation(operationId) {
    try {
      const { data, error } = await supabase?.from('bulk_operations')?.update({
          status: 'cancelled',
          completed_at: new Date()?.toISOString()
        })?.eq('id', operationId)?.eq('status', 'pending')?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Cancel bulk operation error:', error);
      return { data: null, error: error?.message || 'Failed to cancel operation' };
    }
  },

  // Get bulk operation by ID
  async getBulkOperationById(operationId) {
    try {
      const { data, error } = await supabase?.from('bulk_operations')?.select(`
          *,
          created_by:created_by(full_name, email)
        `)?.eq('id', operationId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get bulk operation error:', error);
      return { data: null, error: error?.message || 'Failed to fetch bulk operation' };
    }
  }
};